// src/stores/auth.js — Authentication state
// Faza D.3.1

import { atom, computed } from 'nanostores';

/**
 * Current user object (od backend GET /me):
 *   { id, email, role, tenant_id, tenant_name, permissions, is_master, ... }
 * Lub null gdy nie zalogowany.
 *
 * @type {import('nanostores').WritableAtom<any>}
 */
export const $currentUser = atom(/** @type {any} */ (null));

// Computed helpers
export const $$isLoggedIn = computed($currentUser, (u) => u !== null);
export const $$isMaster = computed($currentUser, (u) => !!(u && u.is_master));
export const $$tenantId = computed($currentUser, (u) => (u ? u.tenant_id : null));

/**
 * Sprawdzenie modułowego RBAC zwracanego przez `/api/auth/me`.
 *
 * `permissions` jest mapą `{ orders: 'write', invoices: 'read', ... }`, nie
 * historyczną tablicą stringów. Dopuszczamy zapis `orders.write` wyłącznie jako
 * wygodny skrót dla starego kodu; źródłem prawdy nadal jest poziom modułu.
 *
 * @param {string} moduleOrPermission np. `orders` albo `orders.write`
 * @param {'read'|'write'} [required]
 */
export function hasPerm(moduleOrPermission, required = 'read') {
  const u = $currentUser.get();
  if (!u) return false;
  if (u.is_master || (u.role && u.role.name === 'master_admin')) return true;

  let moduleKey = String(moduleOrPermission || '');
  const dot = moduleKey.lastIndexOf('.');
  if (dot > 0) {
    const suffix = moduleKey.slice(dot + 1);
    if (suffix === 'read' || suffix === 'write') {
      moduleKey = moduleKey.slice(0, dot);
      required = suffix;
    }
  }
  const level = u.permissions && !Array.isArray(u.permissions)
    ? u.permissions[moduleKey]
    : 'none';
  if (required === 'write') return level === 'write';
  return level === 'read' || level === 'write';
}

/**
 * Role check.
 */
export function userIs(role) {
  const u = $currentUser.get();
  if (!u) return false;
  if (role === 'master' || role === 'master_admin') {
    return !!u.is_master || !!(u.role && u.role.name === 'master_admin');
  }
  return (u.role && typeof u.role === 'object' ? u.role.name : u.role) === role;
}

// Debug
if (typeof window !== 'undefined') {
  window.__sbStores = Object.assign(window.__sbStores || {}, {
    $currentUser,
    $$isLoggedIn,
    $$isMaster,
    $$tenantId,
    hasPerm,
    userIs,
  });
}
